import { create } from 'zustand';
import { invoiceSourcesAPI } from '@matix/api-client';
import type { InvoiceSource } from '@matix/types';

/**
 * Invoice sources — the branch's price lists, and which one the till is on.
 *
 * A source names one of the four `Sale_priceN` tiers; picking one re-prices
 * the catalog and the open cart (see `lib/priceTier.ts`). Loaded once on boot
 * alongside the other tenant config.
 *
 * MOST TENANTS HAVE NO SOURCES. `sources` stays empty, `active` stays null,
 * `tier()` returns null, and every price in the app resolves exactly as it did
 * before this feature existed. The selector is not rendered at all.
 *
 * THE ACTIVE SOURCE IS LOCKED WHILE EDITING AN ORDER. The legacy POS disables
 * its dropdown when an existing order is reopened
 * (order_functions_definitions.js:2238) and so do we: the lines already carry
 * the prices they were sold at, and re-tiering an order mid-edit would restate
 * a receipt the customer has already been given. `locked` is set by the cart
 * when it hydrates an order for editing, and cleared when that edit ends.
 */

interface InvoiceSourceState {
  sources: InvoiceSource[];
  /** Mstr_Pos_InvoiceSource_Setup.Id, or null when the tenant has none. */
  activeId: number | null;
  /** True while an existing order is open for editing — the picker is disabled. */
  locked: boolean;
  loaded: boolean;

  /** The selected source object, or null. */
  active: () => InvoiceSource | null;
  /** The price tier to use, or null when no source is in play. */
  tier: () => 1 | 2 | 3 | 4 | null;
  /** Flat delivery fee the active source adds, or null. */
  deliveryFee: () => number | null;

  setSources: (s: InvoiceSource[]) => void;
  /** Ignored while locked, so an edit-mode UI can never change the tier. */
  setActive: (id: number | null) => void;
  setLocked: (locked: boolean) => void;
  /**
   * Put the till back on a specific source without touching `locked` — used
   * when hydrating an order for editing, so the picker shows the source the
   * order was originally rung up on.
   */
  forceActive: (id: number | null) => void;
  reset: () => void;
}

export const useInvoiceSources = create<InvoiceSourceState>((set, get) => ({
  sources: [],
  activeId: null,
  locked: false,
  loaded: false,

  active: () => get().sources.find((s) => s.id === get().activeId) ?? null,
  tier: () => get().active()?.price_tier ?? null,
  deliveryFee: () => get().active()?.delivery_fee ?? null,

  setSources: (sources) =>
    set((st) => ({
      sources,
      loaded: true,
      // Default to the first source, mirroring the legacy page's auto-click on
      // load. Sources arrive ordered by Code, so "first" is stable across
      // reloads — which matters, because it decides what the next sale costs.
      activeId:
        st.activeId != null && sources.some((s) => s.id === st.activeId)
          ? st.activeId
          : sources[0]?.id ?? null,
    })),

  setActive: (id) => {
    if (get().locked) return;
    set({ activeId: id });
  },

  setLocked: (locked) => set({ locked }),
  forceActive: (activeId) => set({ activeId }),

  reset: () => set({ activeId: null, locked: false }),
}));

/**
 * Fetch the branch's price lists. Errors are swallowed: a till that cannot
 * read this table must still sell, and with no sources it simply prices
 * everything at `selling_price` as it always did.
 */
export async function loadInvoiceSources(): Promise<void> {
  try {
    const r = await invoiceSourcesAPI.list();
    useInvoiceSources.getState().setSources(r.sources);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[invoice-sources] failed to load; pricing stays on the default tier', err);
  }
}
